import React, { useEffect, useRef, useState } from 'react';
import { Button, Checkbox, Select, Spin } from 'antd';

import { errorMessage, findInstruments } from './api';
import { Instrument } from './types';

type Props = {
  value: Instrument | null;
  onChange: (instrument: Instrument) => void;
  disabled?: boolean;
};

const label = (item: Instrument) =>
  item.name === item.ticker ? item.name : `${item.name} · ${item.ticker}`;

const unique = (rows: Instrument[]) =>
  Array.from(new Map(rows.map(item => [item.id, item])).values());

const InstrumentSelect: React.FC<Props> = ({ value, onChange, disabled }) => {
  const [query, setQuery] = useState('');
  /** Only the asset catalogue's vetted tier members. */
  const [vetted, setVetted] = useState(false);
  const fetchPage = (text: string, number: number, signal: AbortSignal) =>
    vetted
      ? findInstruments(text, number, 100, signal, true)
      : findInstruments(text, number, 100, signal);
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<Instrument[]>([]);
  const [count, setCount] = useState(0);
  const [nextPage, setNextPage] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [pageError, setPageError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const generation = useRef(0);
  const pageRequest = useRef<AbortController | null>(null);
  const initialized = useRef(Boolean(value));
  const change = useRef(onChange);
  change.current = onChange;

  useEffect(() => {
    const requestGeneration = ++generation.current;
    const controller = new AbortController();
    pageRequest.current?.abort();
    pageRequest.current = null;
    setLoading(true);
    setLoadingMore(false);
    setRows([]);
    setCount(0);
    setNextPage(null);
    setError('');
    setPageError('');
    const timer = window.setTimeout(
      async () => {
        try {
          const page = await fetchPage(query.trim(), 1, controller.signal);
          if (
            controller.signal.aborted ||
            generation.current !== requestGeneration
          )
            return;
          setRows(unique(page.results));
          setCount(page.count ?? page.results.length);
          setNextPage(page.next_page ?? null);
          if (!initialized.current && !query.trim()) {
            initialized.current = true;
            const preferred = page.results.find(
              item =>
                item.has_daily_history !== false &&
                /eur[\s/_-]*usd/i.test(`${item.name} ${item.ticker}`),
            );
            if (preferred) change.current(preferred);
          }
        } catch (reason) {
          if (
            !controller.signal.aborted &&
            generation.current === requestGeneration
          )
            setError(
              errorMessage(reason) || 'Instruments could not be loaded.',
            );
        } finally {
          if (
            !controller.signal.aborted &&
            generation.current === requestGeneration
          )
            setLoading(false);
        }
      },
      query.trim() ? 250 : 0,
    );
    return () => {
      window.clearTimeout(timer);
      controller.abort();
      pageRequest.current?.abort();
    };
    // fetchPage changes only with `vetted`, which is listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, attempt, vetted]);

  const loadMore = async () => {
    if (!nextPage || loading || pageRequest.current) return;
    const controller = new AbortController();
    pageRequest.current = controller;
    const requestGeneration = generation.current;
    setLoadingMore(true);
    setPageError('');
    try {
      const page = await fetchPage(query.trim(), nextPage, controller.signal);
      if (controller.signal.aborted || generation.current !== requestGeneration)
        return;
      setRows(current => unique([...current, ...page.results]));
      setCount(page.count ?? count);
      setNextPage(page.next_page ?? null);
    } catch (reason) {
      if (
        !controller.signal.aborted &&
        generation.current === requestGeneration
      )
        setPageError(
          errorMessage(reason) || 'More instruments could not be loaded.',
        );
    } finally {
      if (pageRequest.current === controller) {
        pageRequest.current = null;
        if (!controller.signal.aborted) setLoadingMore(false);
      }
    }
  };

  const close = () => {
    setOpen(false);
    setQuery('');
  };

  return (
    <>
      <Select
        id="market-explorer-instrument"
        aria-label="Instrument"
        showSearch
        labelInValue
        filterOption={false}
        placeholder="Search or choose an instrument"
        value={value ? { value: value.id, label: label(value) } : undefined}
        searchValue={query}
        open={open}
        onSearch={text => {
          if (text === query) return;
          setLoading(true);
          setQuery(text);
        }}
        onChange={option => {
          const instrument = rows.find(item => item.id === option.value);
          if (instrument && instrument.has_daily_history !== false) {
            initialized.current = true;
            onChange(instrument);
          }
        }}
        onSelect={close}
        onOpenChange={visible => {
          setOpen(visible);
          if (!visible) setQuery('');
        }}
        onBlur={close}
        onInputKeyDown={event => {
          if (event.key === 'Escape') close();
        }}
        loading={loading || loadingMore}
        disabled={disabled}
        status={error ? 'error' : undefined}
        options={
          loading || error
            ? []
            : rows.map(item => ({
                value: item.id,
                label: `${label(item)}${item.has_daily_history === false ? ' — No daily history' : ''}`,
                disabled: item.has_daily_history === false,
              }))
        }
        notFoundContent={
          loading ? (
            <span role="status">
              <Spin size="small" /> Loading instruments…
            </span>
          ) : error ? (
            'Instrument search is unavailable.'
          ) : query.trim() ? (
            `No instruments match “${query.trim()}”.`
          ) : (
            'No instruments are available.'
          )
        }
        onPopupScroll={event => {
          const list = event.currentTarget;
          if (
            !pageError &&
            list.scrollHeight - list.scrollTop - list.clientHeight < 48
          )
            void loadMore();
        }}
        popupRender={menu => (
          <>
            {menu}
            {!loading && !error && (
              <div
                onMouseDown={event => event.preventDefault()}
                style={{
                  borderTop: '1px solid #edf0f5',
                  padding: '8px 10px',
                  fontSize: 11,
                  color: '#7c899c',
                }}
              >
                {pageError && (
                  <div role="alert" style={{ marginBottom: 4 }}>
                    {pageError}
                  </div>
                )}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                  }}
                >
                  <span>
                    {rows.length.toLocaleString()} of {count.toLocaleString()}{' '}
                    instruments
                  </span>
                  {nextPage && (
                    <Button
                      size="small"
                      type="link"
                      onClick={() => void loadMore()}
                      loading={loadingMore}
                    >
                      {pageError ? 'Retry loading' : 'Load more'}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </>
        )}
        size="large"
      />
      <Checkbox
        checked={vetted}
        disabled={disabled}
        onChange={event => {
          setLoading(true);
          setVetted(event.target.checked);
        }}
        style={{ marginTop: 6, fontSize: 12 }}
      >
        Vetted catalogue only (asset catalogue tiers)
      </Checkbox>
      {error && (
        <div
          role="alert"
          style={{ marginTop: 6, color: '#9a6565', fontSize: 12 }}
        >
          {error}{' '}
          <Button
            type="link"
            size="small"
            onClick={() => setAttempt(current => current + 1)}
          >
            Retry search
          </Button>
        </div>
      )}
    </>
  );
};

export default InstrumentSelect;
